<template>
  <div :class="'base-create ' + (isDisabledComponent ? 'create-study-set-com-disable' : '')">
    <div class="base-create-body">
      <el-form
        :rules="rules"
        :disabled="isDisabledComponent"
        :model="form"
        class="create-edit-form"
        ref="firstForm"
        hide-required-asterisk
      >
        <elsa-input
          v-model="form.name"
          :label="$t('read_aloud.form.read_aloud_name')"
          :placeholder="$t('read_aloud.form.name_your_read_aloud')"
          name="name"
        />
        <el-row class="form-row">
          <el-col :md="12" :xs="24">
            <div class="pick-shareable-to">
              <elsa-select
                popper-class="emails-list-popover"
                name="shareable"
                :information="shareableTooltip"
                :label="$t('study_sets.form.shareable_to')"
                :options="shareableOptionsWithI18n"
                :default-first-option="true"
                :placeholder="$t('study_sets.form.shareable_to')"
                @change="changeShareable"
                v-model.lazy="form.shareable"
                :no-data-text="$t('study_sets.form.no_data_text')"
              />
            </div>
          </el-col>
          <el-col :md="12" :xs="24"> </el-col>
        </el-row>
        <el-row :gutter="20" class="create__row">
          <el-col :span="12" class="create__col-first">
            <el-form-item class="elsa-input text-read-aloud" show-message>
              <template slot="label">
                <div>
                  {{ $t("read_aloud.form.input_reading_text") }}
                  <el-tooltip
                    content="Please note that an item should not exceed 150 words."
                    popper-class="white-space-pre-line"
                  >
                    <i class="el-icon-info" />
                  </el-tooltip>
                </div>
                <span class="count-elem">
                  {{ form.text.split(" ").filter(Boolean).length }}/{{ 150 }} {{ $t("read_aloud.form.words") }}
                </span>
              </template>

              <div v-if="showHighlight" class="highlight-content">
                <span v-html="highlightContent"></span>

                <primary-button
                  v-if="showEditButton"
                  size="large"
                  class="highlight-content__button"
                  @click.native="showHighlight = !showHighlight"
                >
                  {{ showHighlight ? "Edit" : "Cancel" }}
                </primary-button>
                <div :style="{ height: '52px' }">
                  <generate-audio ref="$generateAudio" :disabled="!form.text" :text="form.text" />
                </div>
              </div>
              <div v-else class="text-edit">
                <elsa-input-area
                  v-model="form.text"
                  :placeholder="$t('read_aloud.form.place_holder')"
                  class="content-input-textarea"
                  name="text"
                  type="textarea"
                  size="large"
                  resize="none"
                  :rows="10"
                  :style="{ border: 'none' }"
                />
                <div class="generate-audio">
                  <generate-audio ref="$generateAudio" :disabled="!form.text" :text="form.text" />
                </div>
              </div>
            </el-form-item>
          </el-col>
          <el-col :span="12" class="create__col-second">
            <read-aloud-create-warm-up :disabled="disableButtonGenerateStudyset" @generate="handleGenerateStudySet" />
          </el-col>
        </el-row>
      </el-form>
    </div>
    <read-aloud-image-uploader />
    <checking-modal
      :dialogVisible="showModalSpellCheck"
      append-to-body
      v-on:closed="showModalSpellCheck = false"
      :checkingType="modalCheckType"
      v-on:proceed="handleProceed"
    />
  </div>
</template>

<script>
import GenerateAudio from "@/components/common/GenerateAudioTTS/GenerateAudioTTS.vue";
import ElsaInput from "@/components/common/Input";
import ElsaInputArea from "@/components/common/InputArea";
import { handleFormValidation } from "../../../helpers/formValidationHelper";
import { mapGetters, mapActions, mapState } from "vuex";
import ElsaSelect from "@/components/common/Select";
import { DefaultShareableOptions, DefaultCategoryOptions } from "@/constants/studySets";
import CheckingModal from "@/components/modals/studysets/CheckingModal";
import ReadAloudCreateWarmUp from "./CreateWarmUp";
import PrimaryButton from "@/components/common/buttons/PrimaryButton";
import ReadAloudImageUploader from "./ReadAloudImageUploader";

export default {
  components: {
    ElsaInput,
    ElsaSelect,
    CheckingModal,
    ElsaInputArea,
    PrimaryButton,
    ReadAloudCreateWarmUp,
    ReadAloudImageUploader,
    GenerateAudio,
  },
  props: {
    currentStep: {
      type: Number,
      default: 1,
    },
    componentStep: {
      type: [Number, Array],
      default: 1,
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    tertiaryFunction: {
      type: Function,
      default: null,
    },
    organizationType: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      form: {
        name: "",
        shareable: "group",
        text: "",
      },
      rules: {
        name: { required: true, type: "string", message: this.$t("study_sets.errors.please_provide_name") },
        text: { required: true, type: "string", message: this.$t("read_aloud.errors.please_provide_text") },
        shareable: {
          required: true,
          message: this.$t("study_sets.errors.please_provide_shareable_to"),
          trigger: "change",
        },
        category: { required: true, message: this.$t("study_sets.errors.please_provide_category"), trigger: "change" },
      },
      isDisableGenerateButton: false,
      shareableOptions: DefaultShareableOptions,
      categoryOptions: DefaultCategoryOptions,
      modalCheckType: "limit",
      showModalSpellCheck: false,
      showHighlight: false,
      showEditButton: false,
    };
  },
  async mounted() {
    const readAloudId = +this.$route.params.readAloudId;
    if (readAloudId) {
      // Edit
      const readAloudId = +this.$route.params.readAloudId;
      await this.$store.dispatch("readAlouds/getReadAloud", {
        readAloudId: readAloudId,
      });
    } else {
      // Create
      this.form = this.newReadAloud;
      if (!this.form.text) {
        // clear cache audio link
        this.changeLinkAudio("")
      }
      if (this.highlightContent && this.highlightContent.length) {
        this.showHighlight = true;
        this.showEditButton = true;
      }
    }

    this.$nextTick();
    this.equalHeight();
  },
  watch: {
    detailReadAloud: {
      deep: true,
      handler(value) {
        this.form.name = value.name;
        this.form.shareable = value.shareable;
        this.form.text = value.passage;
      },
    },
    form: {
      deep: true,
      handler(value) {
        if (this.isEditReadAloud) {
          this.updateEditReadAloud(value);
        } else {
          this.updateNewReadAloud(value);
        }

        if (value.text.split(" ").length >= 150) {
          this.form.text = value.text
            .split(" ")
            .slice(0, 150)
            .join(" ");
        }

        this.isDisableGenerateButton = false;
      },
    },

    "form.text": {
      handler(newValue, oldValue) {
        if (newValue === oldValue) return;
        if (!this.audioReadAloudLink) return;
        if (this.showHighlight) return;
        if (!oldValue && !!newValue) return;
        this.$refs &&
          this.$refs.$generateAudio &&
          this.$refs.$generateAudio.handleDeleteAudio &&
          this.$refs.$generateAudio.handleDeleteAudio();
      },
    },

    async "autoWarmUpStudySets.length"(length) {
      this.showHighlight = !!length;

      await this.$nextTick();
      this.equalHeight();
    },
  },
  computed: {
    ...mapGetters({
      studySet: "studysets/getBuildingStudySet",
      currentOrganizationId: "organization/currentOrganizationId",
      categories: "studysets/getStudySetCategories",
      hasChildOrg: "organization/hasChildOrg",
      isTeacherRole: "organization/isOrganizationTeacherOnly",
    }),
    isEditReadAloud() {
      return +this.$route.params.readAloudId;
    },
    ...mapState("readAlouds", [
      "autoWarmUpStudySets",
      "newReadAloud",
      "editReadAloud",
      "warmUpStudySets",
      "finalWarmUpStudySets",
      "detailReadAloud",
      "audioReadAloudLink",
    ]),
    highlightContent() {
      if (!this.autoWarmUpStudySets.length) {
        return "";
      }

      let text = this.form.text;

      if (!text) {
        return "";
      }

      for (const wordObj of this.autoWarmUpStudySets) {
        const word = wordObj.text;
        const regex = new RegExp(`\\b${word}\\b`, "gi");
        const highlightedWord = `<span style="background: #B6D1FF;">${word}</span>`;
        text = text.replace(regex, highlightedWord);
      }

      return text;
    },
    disableButtonGenerateStudyset() {
      if (this.isDisableGenerateButton) return true;

      return !this.form.name || !this.form.text || !this.form.shareable;
    },
    studySetDetails() {
      return this.studySet;
    },
    modalTitle() {
      return this.currentStep === -1
        ? this.$t(this.$i18nLabelsHelper(this.organizationType, "group_details"))
        : this.$t(this.$i18nLabelsHelper(this.organizationType, "step_group_details"));
    },
    isDisabledComponent() {
      if (typeof this.componentStep === "number") {
        return this.currentStep > this.componentStep && this.editMode == false;
      }

      if (Array.isArray(this.componentStep)) {
        return !this.componentStep.includes(this.currentStep) && this.editMode == false;
      }

      return false;
    },
    shareableOptionsWithI18n() {
      let shareableOptionsFilter = this.shareableOptions;
      let filterOptions = [];
      if (this.isTeacherRole) {
        filterOptions.push("organization");
      }

      filterOptions.push("branch");
      shareableOptionsFilter = this.shareableOptions.filter(x => !filterOptions.includes(x.value));
      return shareableOptionsFilter.map(x => {
        return { ...x, label: this.$t(this.$i18nLabelsHelper(this.organizationType, `study_sets.${x.label}`)) };
      });
    },
    shareableTooltip() {
      const tooltipClass = this.$t(
        this.$i18nLabelsHelper(this.organizationType, "content_shareable.tooltip_shareable_to_my_class")
      );
      const tooltioOrg = this.$t("common.content_shareable.tooltip_shareable_to_this_org");
      let tooltip = this.isTeacherRole ? `${tooltipClass}` : `${tooltipClass} \n\n${tooltioOrg}`;
      return tooltip;
    },
  },

  methods: {
    ...mapActions({
      updateBuildingStudySet: "studysets/updateBuildingStudySet",
    }),
    ...mapActions("readAlouds", ["updateNewReadAloud", "updateEditReadAloud", "changeLinkAudio"]),

    handleContinueClick() {
      handleFormValidation(this.$refs.firstForm, this.validFormCallback, () => {});
    },
    async equalHeight() {
      await this.$nextTick();
      const warmUpEl = document.querySelector(".warm-up__form");
      const TextAreaEl = document.querySelector(".text-edit") || document.querySelector(".highlight-content");
      if (!warmUpEl || !TextAreaEl) {
        return;
      }
      TextAreaEl.style.height = "auto";
      warmUpEl.style.height = "auto";
      await setTimeout(() => {}, 100);
      const warupHeight = warmUpEl.offsetHeight;
      const textAreaHeight = TextAreaEl.offsetHeight;
      const heightest = warupHeight > textAreaHeight ? warupHeight : textAreaHeight;

      TextAreaEl.style.height = `${heightest}px`;
      warmUpEl.style.height = `${heightest}px`;
    },
    validFormCallback() {
      const idxSpellError = this.finalWarmUpStudySets.findIndex(tag => tag.spell_error);
      const idxLimitationError = this.finalWarmUpStudySets.findIndex(tag => tag.limit_error);
      if (idxLimitationError > -1 || idxSpellError > -1) {
        this.showModalSpellCheck = true;
        this.modalCheckType = idxLimitationError > -1 ? "limit" : "spell";
      } else this.$emit("next-step", this.currentStep);
    },
    handleProceed() {
      this.$emit("next-step", this.currentStep);
      this.showModalSpellCheck = false;
    },
    handleCancelClick() {
      return this.$router.push({
        name: "classes",
        params: {
          organizationId: this.currentOrganizationId,
        },
      });
    },
    changeCategory(category) {
      this.updateBuildingStudySet({
        category: category,
      });
    },
    changeShareable(shareable) {
      this.updateBuildingStudySet({
        shareable,
      });
    },
    setTags(newPhrases) {
      this.updateBuildingStudySet({
        phrases: newPhrases,
      });
    },
    handleGenerateStudySet() {
      if (this.isEditReadAloud) {
        this.updateEditReadAloud(this.form);
      } else {
        this.updateNewReadAloud(this.form);
      }

      this.isDisableGenerateButton = true;
      this.showHighlight = true;
      this.showEditButton = true;
      this.$emit("generate");
    },
  },
};
</script>

<style lang="scss" scoped>
.create {
  &__row {
    width: 100%;
  }

  &__col-first {
    padding-left: 0;
  }

  &__col-second {
    padding-right: 0 !important;
  }
}

.passage-content {
  height: 400px;
}

.highlight-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  background: #ffffff;
  border: 1px solid #e6e6e6;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  border-radius: 8px;
  font-family: "SF Pro Display", sans-serif;
  font-size: 16px;
  line-height: 24px;
  color: #121131;
  padding: 25px;
  height: 400px;

  &__button {
    margin-top: auto;
    width: 200px;
  }
}

.el-form {
  padding-bottom: 0px !important;
}

.form-row {
  margin-top: 20px;
}

.pick-category {
  font-weight: 600;
  font-size: 14px;
  color: #478dff;
  flex: 1;
  padding-left: 12px;
}

.pick-shareable-to {
  font-weight: 600;
  font-size: 14px;
  color: #478dff;
  flex: 1;
  padding-right: 12px;
}

.create-edit-form .form-row .el-form-item.elsa-input:not(:last-of-type) {
  margin-right: 0;
}

.input-phrases {
  width: 100%;
  height: 160px;
}

::v-deep .elsa-section {
  background-color: #f2f7fa;
}

::v-deep .el-date-editor.el-input {
  width: 200px;
}

::v-deep .elsa-input .el-input .el-input__inner {
  width: 100%;
  color: #121131;
  opacity: 1;
}

::v-deep .elsa-input .el-select .el-input__inner {
  color: #121131 !important;
  opacity: 1 !important;
}

::v-deep .elsa-tags-input {
  min-height: 240px;

  .ti-input .ti-new-tag-input {
    color: #b4b4b4;
    font-family: "SF Pro Display";
    font-size: 16px;
    line-height: 24px;
  }
}

::v-deep .ti-tag {
  background: #e9e9ec !important;
  border-radius: 4px !important;
}

::v-deep .el-form-item__label {
  line-height: 24px !important;
  font-weight: 600 !important;
  color: #121131 !important;
}

.text-read-aloud {
  ::v-deep(textarea) {
    height: 400px;
    padding: 25px;
  }

  ::v-deep .el-form-item__label {
    justify-content: space-between;
  }
}

.text-edit {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border: 1px solid #e6e6e6;
  border-radius: 8px;

  ::v-deep .el-textarea textarea {
    border: none;
    width: 100%;
    height: 100%;
  }
}

.content-input-textarea {
  margin: 15px;
}

.button-auto-genarate {
  ::v-deep .el-form-item__content {
    display: flex;
    justify-content: flex-end;
  }

  ::v-deep .el-button {
    min-width: 160px;
    border-radius: 8px;
    padding: 0 10px;
    font-family: "SF Pro Display", sans-serif;
    font-size: 16px;
    line-height: 24px;
    color: #000;
    height: 56px;
    border: 2px solid #e9e9e9;

    &.is-disabled {
      background-color: #e9e9e9;
      color: #fff;
    }
  }
}

.generate-audio {
  margin-top: 12px;
  padding: 12px;
  padding-bottom: 16px;
}
</style>
